import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../apiClient';
import '../SettingsCommon.css';
import AdChannelsPanel from './AdChannelsPanel';

/* FieldRoutes is the only provider anything reads. The "Sunpest" row saved a
   TenantIntegration kind nothing consumes (0 rows platform-wide), so it is
   gone rather than kept as a switch that does nothing.

   The create contract (accounts/views/integration_api.py) needs the API key,
   the API token AND the account subdomain; the page asked for neither the
   subdomain nor treated the token as required, so every new connection 400'd.
   Enabling with no credentials sent nothing and still said "saved". */
const DEFAULT_STATE = {
  fieldroutes_enabled: false,
  fieldroutes_api_key: '',
  fieldroutes_api_secret: '',
  fieldroutes_subdomain: '',
  fieldroutes_integration_id: null,
  fieldroutes_has_api_key: false,
};

const ITEMS = [
  { key: 'fieldroutes', label: 'FieldRoutes', description: 'Customer and appointment sync' },
];

const providers = ['fieldroutes'];

const serverDetail = (err) => err?.response?.data?.detail || err?.message || '';

export default function IntegrationsPage() {
  const [state, setState] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let mounted = true;
    const loadIntegrations = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/integrations/');
        const rows = Array.isArray(data) ? data : data?.results || [];
        const next = { ...DEFAULT_STATE };
        rows.forEach((row) => {
          const provider = String(row?.provider || '').toLowerCase();
          if (!providers.includes(provider)) return;
          next[`${provider}_enabled`] = !!row?.enabled;
          next[`${provider}_integration_id`] = row?.id || null;
          next[`${provider}_has_api_key`] = !!(row?.has_api_key || row?.has_auth_key);
          next[`${provider}_subdomain`] = row?.subdomain || '';
        });
        if (!mounted) return;
        setState(next);
      } catch {
        if (mounted) setState(DEFAULT_STATE);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void loadIntegrations();
    return () => {
      mounted = false;
    };
  }, []);

  const enabledCount = useMemo(
    () => providers.filter((provider) => !!state[`${provider}_enabled`]).length,
    [state]
  );

  const onToggle = (key) => setState((prev) => ({ ...prev, [key]: !prev[key] }));

  const onChange = (event) => {
    const { name, value } = event.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    const problems = [];
    let wrote = false;
    try {
      for (const provider of providers) {
        const integrationId = state[`${provider}_integration_id`];
        const enabled = !!state[`${provider}_enabled`];
        const apiKey = String(state[`${provider}_api_key`] || '').trim();
        const apiSecret = String(state[`${provider}_api_secret`] || '').trim();
        const subdomain = String(state[`${provider}_subdomain`] || '').trim();
        const payload = { provider, enabled };
        if (apiKey) payload.api_key = apiKey;
        if (apiSecret) payload.api_secret = apiSecret;
        if (subdomain) payload.subdomain = subdomain;

        if (integrationId) {
          const { data } = await api.patch(`/integrations/${integrationId}/`, payload);
          wrote = true;
          setState((prev) => ({
            ...prev,
            [`${provider}_enabled`]: !!data?.enabled,
            [`${provider}_has_api_key`]: !!(data?.has_api_key || data?.has_auth_key) || prev[`${provider}_has_api_key`],
          }));
          continue;
        }

        if (!enabled && !apiKey) continue; // nothing asked of this provider
        if (!apiKey || !apiSecret || !subdomain) {
          problems.push('FieldRoutes needs the API key, the API token and your FieldRoutes subdomain to connect.');
          continue;
        }
        const { data } = await api.post('/integrations/', payload);
        wrote = true;
        setState((prev) => ({
          ...prev,
          [`${provider}_integration_id`]: data?.id || prev[`${provider}_integration_id`],
          [`${provider}_has_api_key`]: !!(data?.has_api_key || data?.has_auth_key),
        }));
      }
      if (problems.length) setMessage(problems.join(' '));
      else setMessage(wrote ? 'Integration settings saved.' : 'Nothing to save.');
      if (!problems.length) {
        setState((prev) => ({ ...prev, fieldroutes_api_key: '', fieldroutes_api_secret: '' }));
      }
    } catch (err) {
      const detail = serverDetail(err);
      setMessage(detail ? `Unable to save integration settings: ${detail}` : 'Unable to save integration settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='settings-page'>
        <p>Loading integrations…</p>
      </div>
    );
  }

  return (
    <div className='settings-page'>
      <h2>Integrations</h2>
      <p className='settings-sub'>Manage active provider connections for communications, payments, and routing.</p>
      {message ? <p className='settings-msg'>{message}</p> : null}

      {/* Ad accounts own their own load/save cycle (live-verified status, write-only
          credentials) and deliberately do NOT share this page's save bar. */}
      <AdChannelsPanel />

      <div className='settings-card'>
        <div className='row-between'>
          <h3>Connection Status</h3>
          <span className='settings-pill'>{enabledCount} active</span>
        </div>
        <div className='settings-list-grid'>
          {ITEMS.map((item) => {
            const provider = item.key;
            return (
            <label key={provider} className='settings-integration-row'>
              <div>
                <strong>{item.label}</strong>
                <p>{item.description}</p>
                <p>
                  {state[`${provider}_has_api_key`]
                    ? 'API key configured'
                    : 'No API key configured'}
                </p>
              </div>
              <input
                type='checkbox'
                checked={!!state[`${provider}_enabled`]}
                onChange={() => onToggle(`${provider}_enabled`)}
              />
            </label>
            );
          })}
        </div>
      </div>

      <div className='settings-card two-col'>
        <label>
          FieldRoutes subdomain
          <input
            name='fieldroutes_subdomain'
            value={state.fieldroutes_subdomain}
            onChange={onChange}
            placeholder='yourcompany (from yourcompany.fieldroutes.com)'
          />
        </label>
        <label>
          FieldRoutes API Key
          <input
            name='fieldroutes_api_key'
            value={state.fieldroutes_api_key}
            onChange={onChange}
            placeholder={state.fieldroutes_has_api_key ? 'Configured — enter a new key to replace it' : 'Enter FieldRoutes API key'}
          />
        </label>
        <label>
          FieldRoutes API Token
          <input
            name='fieldroutes_api_secret'
            value={state.fieldroutes_api_secret}
            onChange={onChange}
            placeholder={state.fieldroutes_has_api_key ? 'Configured — enter a new token to replace it' : 'Enter FieldRoutes API token'}
          />
        </label>
      </div>

      <button type='button' className='settings-primary' onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save Integration Settings'}
      </button>
    </div>
  );
}
